import React, { useState, useEffect, useMemo } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../Firebase/firebaseConfig";
import { useTranslation } from "react-i18next";
import fallbackImage from "../../Assets/fallback.jpg";
import { Modal, Spinner, Alert } from "react-bootstrap";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./Library.css";

const DEFAULT_FILTERS = { query: "", year: "all", language: "all" };

const pick = (field, lang) =>
  typeof field === "object" && field !== null
    ? field[lang] || field.en || Object.values(field).find(Boolean) || ""
    : field || "";

const PublishingHouseIntro = () => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const icons = { mission: "fa-bullseye", vision: "fa-eye", team: "fa-users" };

  return (
    <section className="pub-intro">
      <div className="container">
        <div className="pub-about">
          <span className="ccf-eyebrow">{t("ourCollection")}</span>
          <h2 className="pub-about__title">{t("publishingHouse")}</h2>
          <div className="heading-line heading-line--start"></div>
          <p className={`pub-about__text ${expanded ? "is-expanded" : ""}`}>{t("sectionDescription")}</p>
          <button className="pub-about__toggle" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
            {expanded ? t("showLess", "Show less") : t("readMore")} <i className={`fas fa-chevron-${expanded ? "up" : "down"}`}></i>
          </button>
        </div>

        <div className="pub-intro__grid">
          {["mission", "vision", "team"].map((key) => (
            <div className="pub-feature" key={key}>
              <span className="pub-feature__icon"><i className={`fas ${icons[key]}`}></i></span>
              <h3>{t(`features.${key}`)}</h3>
              <p>{t(`features.${key}Description`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const BookCard = ({ book, onOpen }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const title = pick(book.name, lang) || t("untitled");
  const author = pick(book.author, lang);

  return (
    <article className="book-card" onClick={() => onOpen(book)}>
      <div className="book-card-image-wrapper">
        <img
          src={book.coverImageUrl || fallbackImage}
          alt={title}
          className="book-card-image"
          loading="lazy"
          onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
        />
        {book.year && <span className="book-year">{book.year}</span>}
      </div>
      <div className="book-card-content">
        <h3 className="book-title">{title}</h3>
        {author && <p className="book-author">{author}</p>}
        <button
          className="read-more-btn"
          onClick={(e) => { e.stopPropagation(); onOpen(book); }}
          aria-label={`${t("readMore")}: ${title}`}
        >
          {t("readMore")} <i className="fas fa-arrow-right"></i>
        </button>
      </div>
    </article>
  );
};

const LibraryView = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sort, setSort] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "books"),
      (snapshot) => {
        setBooks(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      },
      () => {
        setError(t("operationFailed"));
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [t]);

  const yearOf = (b) => (b.year ? String(b.year) : "");
  const languageOf = (b) => pick(b.originalLanguage, lang);

  const countFacet = (getter) => {
    const counts = {};
    books.forEach((b) => { const v = getter(b); if (v) counts[v] = (counts[v] || 0) + 1; });
    return Object.entries(counts);
  };
  const years = useMemo(() => countFacet(yearOf).sort((a, b) => Number(b[0]) - Number(a[0])), [books]); // eslint-disable-line react-hooks/exhaustive-deps
  const languages = useMemo(() => countFacet(languageOf).sort((a, b) => b[1] - a[1]), [books, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    const list = books.filter(
      (b) =>
        (filters.year === "all" || yearOf(b) === filters.year) &&
        (filters.language === "all" || languageOf(b) === filters.language) &&
        (!q ||
          pick(b.name, lang).toLowerCase().includes(q) ||
          pick(b.author, lang).toLowerCase().includes(q))
    );
    const title = (b) => pick(b.name, lang);
    return [...list].sort((a, b) => {
      if (sort === "oldest") return Number(a.year || 0) - Number(b.year || 0);
      if (sort === "az") return title(a).localeCompare(title(b), lang);
      return Number(b.year || 0) - Number(a.year || 0);
    });
  }, [books, filters, sort, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const clearFilters = () => setFilters(DEFAULT_FILTERS);
  const isFiltered = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  const Option = ({ name, value, label, count }) => (
    <label className={`pub-option ${filters[name] === value ? "is-active" : ""}`}>
      <input type="radio" name={name} checked={filters[name] === value} onChange={() => setFilter(name, value)} />
      <span className="pub-option__dot"></span>
      <span className="pub-option__label">{label}</span>
      <span className="pub-option__count">{count}</span>
    </label>
  );

  const activeChips = [
    filters.year !== "all" && { key: "year", label: filters.year },
    filters.language !== "all" && { key: "language", label: filters.language },
    filters.query && { key: "query", label: `“${filters.query}”` },
  ].filter(Boolean);

  const ledger = selectedBook && [
    [t("author", "Author"), pick(selectedBook.author, lang) || t("unknown", "Unknown")],
    [t("translator", "Translator"), pick(selectedBook.translator, lang) || t("unknown", "Unknown")],
    [t("originalLanguage", "Original language"), pick(selectedBook.originalLanguage, lang) || t("unknown", "Unknown")],
    [t("year"), selectedBook.year || t("unknownYear", "Unknown")],
  ];

  return (
    <div className="pub-page">
      {/* Hero band */}
      <header className="pub-hero">
        <div className="container">
          <span className="pub-hero__eyebrow">Centro Culturale Francescano</span>
          <h1 className="pub-hero__title">{t("navbar.publication", "Publications")}</h1>
          <div className="pub-hero__line"></div>
          <ul className="pub-hero__stats">
            <li><strong>{books.length}</strong> {t("publications", "publications")}</li>
            {languages.length > 0 && <li><strong>{languages.length}</strong> {t("languages", "languages")}</li>}
          </ul>
        </div>
      </header>

      <PublishingHouseIntro />

      {/* Collection */}
      <section className="container pub-layout">
        <aside className={`pub-sidebar ${showFilters ? "is-open" : ""}`} aria-label="Filters">
          <div className="pub-sidebar__head">
            <h2 className="pub-sidebar__title"><i className="fas fa-sliders-h"></i> {t("filters", "Filters")}</h2>
            {isFiltered && <button className="pub-clear" onClick={clearFilters}>{t("clearAll", "Clear all")}</button>}
          </div>

          <div className="pub-facet pub-facet--search">
            <div className="pub-search">
              <i className="fas fa-search" aria-hidden="true"></i>
              <input
                type="search"
                className="form-control"
                placeholder={t("searchBooks", "Title or author…")}
                aria-label={t("searchBooks", "Title or author…")}
                value={filters.query}
                onChange={(e) => setFilter("query", e.target.value)}
              />
            </div>
          </div>

          {languages.length > 0 && (
            <fieldset className="pub-facet">
              <legend>{t("originalLanguage", "Original language")}</legend>
              <Option name="language" value="all" label={t("all", "All")} count={books.length} />
              {languages.map(([l, n]) => <Option key={l} name="language" value={l} label={l} count={n} />)}
            </fieldset>
          )}

          {years.length > 0 && (
            <fieldset className="pub-facet">
              <legend>{t("year")}</legend>
              <Option name="year" value="all" label={t("all", "All")} count={books.length} />
              {years.map(([y, n]) => <Option key={y} name="year" value={y} label={y} count={n} />)}
            </fieldset>
          )}
        </aside>

        <main className="pub-results">
          <div className="pub-toolbar">
            <button className="pub-filter-toggle" onClick={() => setShowFilters((s) => !s)} aria-expanded={showFilters}>
              <i className="fas fa-sliders-h"></i> {t("filters", "Filters")}
              {isFiltered && <span className="pub-filter-toggle__dot"></span>}
            </button>
            <h2 className="collection-title">
              {t("ourCollection")} <span>{visible.length}</span>
            </h2>
            <label className="pub-sort">
              <span>{t("sortBy", "Sort by")}</span>
              <select className="form-select" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="newest">{t("sortNewest", "Newest first")}</option>
                <option value="oldest">{t("sortOldest", "Oldest first")}</option>
                <option value="az">{t("sortAz", "Title A–Z")}</option>
              </select>
            </label>
          </div>

          {activeChips.length > 0 && (
            <div className="pub-chips">
              {activeChips.map((c) => (
                <button key={c.key} className="pub-chip" onClick={() => setFilter(c.key, DEFAULT_FILTERS[c.key])}>
                  {c.label} <i className="fas fa-times"></i>
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="loading-container"><Spinner animation="border" /></div>
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : books.length === 0 ? (
            <Alert variant="info">{t("noBooksAvailable")}</Alert>
          ) : visible.length === 0 ? (
            <div className="pub-empty">
              <i className="fas fa-book-open"></i>
              <p>{t("noBooksAvailable")}</p>
              <button className="btn btn-outline-primary" onClick={clearFilters}>{t("clearAll", "Clear all")}</button>
            </div>
          ) : (
            <div className="books-grid">
              {visible.map((book) => <BookCard key={book.id} book={book} onOpen={setSelectedBook} />)}
            </div>
          )}
        </main>
      </section>

      {/* Details modal */}
      {selectedBook && (
        <Modal show onHide={() => setSelectedBook(null)} centered size="lg" className="book-modal">
          <Modal.Header closeButton>
            <Modal.Title>{pick(selectedBook.name, lang) || t("untitled")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="book-detail">
              <div className="book-detail__cover">
                <img
                  src={selectedBook.coverImageUrl || fallbackImage}
                  alt={pick(selectedBook.name, lang)}
                  onError={(e) => { e.target.onerror = null; e.target.src = fallbackImage; }}
                />
              </div>
              <div className="book-detail__info">
                <dl className="book-ledger">
                  {ledger.map(([label, value]) => (
                    <div className="book-ledger__row" key={label}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                    </div>
                  ))}
                </dl>
                <h4 className="book-detail__heading">{t("description")}</h4>
                <p className="book-detail__text">{pick(selectedBook.description, lang) || t("noDescription")}</p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className="btn btn-outline-primary" onClick={() => setSelectedBook(null)}>{t("close")}</button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default LibraryView;
